import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthTokens, AuthUser } from '../types/auth.types';
import { zumoTokenFor } from '../api/auth.mappers';

interface AuthState {
  tokens: AuthTokens | null;
  user: AuthUser | null;
  /** True once we've hydrated from storage AND resolved the initial session. */
  isInitialized: boolean;

  setTokens: (tokens: AuthTokens | null) => void;
  /**
   * Merge the EasyAuth token of one MEDIS host into the session's host map —
   * the lazy per-host issue (`lib/medis/zumoToken.ts`). No-op without a session.
   */
  setZumoHostToken: (host: string, token: string) => void;
  setUser: (user: AuthUser | null) => void;
  setInitialized: (value: boolean) => void;
  /** Wipe the entire session (logout / refresh failure). */
  clear: () => void;
}

const STORAGE_KEY = 'gral.auth';

/**
 * Auth session store.
 *
 * Tokens are persisted to localStorage so a page refresh keeps the user signed
 * in. This is a deliberate trade-off for a no-backend SPA: there is no httpOnly
 * cookie option without a server, so we mirror the mobile app's local-storage
 * model. The access token is short-lived and rotated via the refresh flow.
 *
 * NOTE: accessed imperatively from the axios interceptors via `useAuthStore
 * .getState()` — that is intentional and the reason tokens live in a store
 * rather than React context.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      tokens: null,
      user: null,
      isInitialized: false,

      setTokens: (tokens) => set({ tokens }),
      setZumoHostToken: (host, token) =>
        set((state) =>
          state.tokens
            ? {
                tokens: {
                  ...state.tokens,
                  xZumoAuthHosts: { ...state.tokens.xZumoAuthHosts, [host.toLowerCase()]: token },
                },
              }
            : state,
        ),
      setUser: (user) => set({ user }),
      setInitialized: (isInitialized) => set({ isInitialized }),
      clear: () => set({ tokens: null, user: null }),
    }),
    {
      name: STORAGE_KEY,
      // Only persist the session itself, never the transient init flag.
      partialize: (state) => ({ tokens: state.tokens, user: state.user }),
    },
  ),
);

/** Non-reactive selectors for use outside React (interceptors, services). */
export const authSession = {
  getAccessToken: () => useAuthStore.getState().tokens?.accessToken ?? null,
  getRefreshToken: () => useAuthStore.getState().tokens?.refreshToken ?? null,
  /** EasyAuth token for the host of `url` (see `zumoTokenFor`). */
  getZumoToken: (url?: string) => zumoTokenFor(useAuthStore.getState().tokens, url),
  /** EasyAuth token the host map holds for `host` (hostname, no port); null when absent or legacy. */
  getZumoHostToken: (host: string) =>
    useAuthStore.getState().tokens?.xZumoAuthHosts?.[host.toLowerCase()] ?? null,
  /** File the token the auth server issued on demand for `host` (see `lib/medis/zumoToken.ts`). */
  setZumoHostToken: (host: string, token: string) =>
    useAuthStore.getState().setZumoHostToken(host, token),
  /** True when the session has per-host EasyAuth tokens (legacy sessions carry only one). */
  hasZumoHostMap: () => Boolean(useAuthStore.getState().tokens?.xZumoAuthHosts),
  /**
   * True only for a session issued before the per-host model: a single legacy
   * token and no map. A session with NEITHER (the grant's exchange failed) is
   * still per-host capable — it asks `/v1/zumo-token` like any other.
   */
  hasLegacyZumoOnly: () => {
    const tokens = useAuthStore.getState().tokens;
    return Boolean(tokens && !tokens.xZumoAuthHosts && tokens.xZumoAuth);
  },
  isAuthenticated: () => Boolean(useAuthStore.getState().tokens?.accessToken),
};
